import { useState, useEffect } from 'react';
import axios from 'axios';

const normalizeQuestions = (questions) => {
  return questions.map(q => {
    const answerMap = {
      [q.optionA]: 'a',
      [q.optionB]: 'b',
      [q.optionC]: 'c',
      [q.optionD]: 'd',
    };

    return {
      ...q,
      options: {
        a: q.optionA,
        b: q.optionB,
        c: q.optionC,
        d: q.optionD,
      },
      correctAnswer: answerMap[q.correctAnswer] || q.correctAnswer.toLowerCase()[0],
    };
  });
};

export const useDailyTrivia = () => {
  const [showPopup, setShowPopup] = useState(false);
  const [triviaQuestions, setTriviaQuestions] = useState([]);
  const [hasNewTrivia, setHasNewTrivia] = useState(false);

  const checkAndFetchTrivia = () => {
    const today = new Date().toDateString();
    const lastTriviaDate = localStorage.getItem('triviaDateGenerated');
    const storedTrivia = localStorage.getItem('triviaQuestions');
    const triviaCompleted = localStorage.getItem('triviaCompleted');

    // Skip if no token (not logged in)
    if (!sessionStorage.getItem('token')) return;

    // Already completed today → no popup/badge
    if (triviaCompleted === today) {
      setHasNewTrivia(false);
      return;
    }

    // Already generated today → load from storage
    if (lastTriviaDate === today && storedTrivia) {
      try {
        const questions = JSON.parse(storedTrivia);
        const normalized = normalizeQuestions(questions);
        setTriviaQuestions(normalized);
        setHasNewTrivia(true);
        // Show popup only if not dismissed
        if (localStorage.getItem('triviaDismissed') !== 'true') {
          setShowPopup(true);
        }
      } catch (error) {
        console.error('Failed to parse stored trivia:', error);
      }
      return;
    }

    // Fetch new trivia (new day or missing data)
    const fetchTrivia = async () => {
      try {
        const response = await axios.post(
          `${process.env.REACT_APP_BACKEND_URL_WEB}/Trivia/generate`,
          {
            numberOfQuestions: 5,
            audienceType: "others",
            designation: sessionStorage.getItem("role") || "General",
          }
        );

        if (response.data?.success) {
          const questions = response.data.data || [];
          const normalized = normalizeQuestions(questions);
          setTriviaQuestions(normalized);
          setHasNewTrivia(true);
          setShowPopup(true);

          localStorage.setItem('triviaDateGenerated', today);
          localStorage.setItem('triviaQuestions', JSON.stringify(questions));
          localStorage.removeItem('triviaDismissed');
          localStorage.removeItem('triviaCompleted');

          // Notify sidebar
          window.dispatchEvent(new Event('triviaFetched'));
        }
      } catch (error) {
        console.error('Failed to fetch daily trivia:', error);
      }
    };

    fetchTrivia();
  };

  useEffect(() => {
    // Run once on mount
    checkAndFetchTrivia();

    // Listen for token ready (after login)
    const handleTokenReady = () => {
      checkAndFetchTrivia();
    };

    // Also listen for trivia completion to update state
    const handleTriviaCompleted = () => {
      setHasNewTrivia(false);
    };

    window.addEventListener('tokenReady', handleTokenReady);
    window.addEventListener('triviaCompleted', handleTriviaCompleted);

    return () => {
      window.removeEventListener('tokenReady', handleTokenReady);
      window.removeEventListener('triviaCompleted', handleTriviaCompleted);
    };
  }, []);

  const handleSkip = () => {
    setShowPopup(false);
    localStorage.setItem('triviaDismissed', 'true');
  };

  const handleLetsGo = (navigate) => {
    setShowPopup(false);
    navigate('/trivia');
  };

  const markTriviaAsCompleted = () => {
    const today = new Date().toDateString();
    localStorage.setItem('triviaCompleted', today);
    window.dispatchEvent(new Event('triviaCompleted'));
  };

  return {
    showPopup,
    triviaQuestions,
    handleLetsGo,
    handleSkip,
    hasNewTrivia,
    markTriviaAsCompleted,
  };
};